/**
 * What: Destination loading frames and connection recovery outside connected product content.
 * Transport / data-flow: Projects authoritative readiness; reads the merchant-authenticated pricing link.
 * Invariants: Loading exposes no connected-account data or actions before a verified connection exists.
 */
import { launchAdmissionCopy, productLanguage, shopifyAppLoadingCopy } from '@testfeed/product-language'
import { buildSettingsViewUrl, type ShopifyAppViewState } from '../resultsNavigation'
import { pageTitleForView } from '../shopifyPageCopy'
import { Banner, BlockStack, Button, Text } from '@shopify/polaris'
import type { TestFeedConnectionStatus } from '../types'
import type { VerifiedTestFeedConnectionScope } from '../ShopifyAppContext'
import { ShopifyPageContent } from './ShopifyPageContent'
import { ShopifyPageSkeleton } from './ShopifyPageSkeleton'
import { HomeSkeleton } from './app-home/parts/Small'
import { TestFeedReadinessCard } from './TestFeedReadinessCard'
import { useShopifyPlanSelectionUrl } from '../hooks/useShopifyPlanSelectionUrl'
import type { ShopifyAppBridge } from '../shopifyApiFetch'
import { trackShopifyRefetch } from '../shopifyUiTracking'

export type ConnectionPresentationState = 'checking' | 'connected' | 'disconnected' | 'needs-connection' | 'failed'

export function resolveConnectionPresentationState({
  readiness,
  verifiedScope,
  isLoading,
}: {
  readiness: TestFeedConnectionStatus | null | undefined
  verifiedScope: VerifiedTestFeedConnectionScope | null
  isLoading: boolean
}): ConnectionPresentationState {
  // A previously verified scope remains authoritative while React Query refreshes it.
  if (verifiedScope) return 'connected'
  if (isLoading && !readiness) return 'checking'
  if (!readiness) return 'failed'
  if (readiness.code === 'READY') return 'failed'
  if (
    readiness.code === 'TESTFEED_CONNECTION_STATUS_UNAVAILABLE' ||
    readiness.code === 'TESTFEED_INTERNAL_API_SECRET_MISSING'
  )
    return 'failed'
  if (readiness.code === 'SHOPIFY_CONNECTION_DISCONNECTED' || readiness.code === 'SHOPIFY_CONNECTION_UNINSTALLED') {
    return 'disconnected'
  }
  // Known configuration states retain recovery without mounting the product routes.
  return 'needs-connection'
}

/** Keep the requested page visible while account scope is loading, without mounting its data consumers. */
export function ShopifyPageLoading({ view }: { view: ShopifyAppViewState['view'] }) {
  if (view === 'home') return <HomeSkeleton />
  let layout: 'content' | 'index' = 'content'
  if (view === 'studies') layout = 'index'
  return (
    <s-page heading={pageTitleForView(view)}>
      <ShopifyPageContent>
        <ShopifyPageSkeleton layout={layout} accessibilityLabel={shopifyAppLoadingCopy.loading} />
      </ShopifyPageContent>
    </s-page>
  )
}

export function ShopifyConnectionStatusView({
  state,
  shopify,
  readiness,
  onConnect,
  isConnecting,
}: {
  state: Exclude<ConnectionPresentationState, 'checking'>
  shopify: ShopifyAppBridge
  readiness: TestFeedConnectionStatus | null
  onConnect: () => void
  isConnecting: boolean
}) {
  // Shopify pricing needs the merchant session, not the TestFeed account being linked.
  const plan = useShopifyPlanSelectionUrl(shopify, Boolean(readiness))
  let content = (
    <TestFeedReadinessCard
      readiness={readiness}
      isLoading={false}
      isConnecting={isConnecting}
      isDisconnecting={false}
      connectionError={null}
      planSelectionUrl={plan.data}
      onConnect={onConnect}
      onDisconnect={() => undefined}
    />
  )
  let heading: string = launchAdmissionCopy.title
  let description: string = launchAdmissionCopy.gateDescription
  if (state === 'failed') {
    content = (
      <div role="alert" style={{ padding: '2rem' }}>
        <Banner title={launchAdmissionCopy.checkFailedTitle} tone="critical">
          <Text as="p">{launchAdmissionCopy.checkFailedDescription}</Text>
        </Banner>
      </div>
    )
    heading = launchAdmissionCopy.checkFailedTitle
    description = launchAdmissionCopy.checkFailedDescription
  }
  return (
    <s-page heading={heading} inlineSize="small">
      <ShopifyPageContent>
        <BlockStack gap="400">
          <s-section>
            <s-paragraph>{description}</s-paragraph>
          </s-section>
          {content}
          {plan.error && (
            <s-banner tone="warning" heading={launchAdmissionCopy.planUnavailable}>
              <s-button
                onClick={() => void trackShopifyRefetch(shopify, () => plan.refetch())}
                loading={plan.isFetching}
              >
                {launchAdmissionCopy.refresh}
              </s-button>
            </s-banner>
          )}
        </BlockStack>
      </ShopifyPageContent>
    </s-page>
  )
}

/** Explains the existing builder connection prerequisite. */
export function BuilderConnectionReadinessBanner({ visible }: { visible: boolean }) {
  if (!visible) return null
  const copy = productLanguage.shopify.studyBuilder.readinessBanners.builderBlockedForTestFeedReadiness
  return (
    <Banner title={copy.title} tone="warning">
      <BlockStack gap="300">
        <Text as="p">{copy.body}</Text>
        <Button url={buildSettingsViewUrl()}>{copy.action}</Button>
      </BlockStack>
    </Banner>
  )
}
